const STORAGE_KEY = "sportsmapRoutes";

export const routeStore = new Map();

const DEFAULT_STYLE = {
  color: "#000000",
  thickness: 5,
  startColor: "#000000",
  endColor: "#000000",
  showStartPoint: true,
  showEndPoint: true,
};

function normalizeRouteStyle(style = {}) {
  const legacyShowEndPoints = style.showEndPoints;
  const normalized = {
    ...DEFAULT_STYLE,
    ...style,
    showStartPoint:
      style.showStartPoint ?? legacyShowEndPoints ?? DEFAULT_STYLE.showStartPoint,
    showEndPoint:
      style.showEndPoint ?? legacyShowEndPoints ?? DEFAULT_STYLE.showEndPoint,
  };

  delete normalized.showEndPoints;
  return normalized;
}

export function getNextRouteId() {
  const random = Math.random().toString(36).slice(2, 7);
  return `route-${Date.now().toString(36)}-${random}`;
}

export function normalizeRoute(route = {}) {
  return {
    id: String(route.id || getNextRouteId()),
    name: route.name || "Route",
    geojson: route.geojson || null,
    geojsonUrl: route.geojsonUrl || null,
    source: route.source || { type: "upload" },
    style: normalizeRouteStyle(route.style),
  };
}

export function addRoute(route) {
  const normalized = normalizeRoute(route);
  routeStore.set(normalized.id, normalized);
  return normalized;
}

export function updateRoute(id, patch) {
  const current = routeStore.get(String(id));
  if (!current) return null;

  const updated = {
    ...current,
    ...patch,
    style: {
      ...current.style,
      ...(patch.style || {}),
    },
  };

  routeStore.set(String(id), updated);
  return updated;
}

export function removeRoute(id) {
  return routeStore.delete(String(id));
}

export function clearRoutes() {
  routeStore.clear();
  clearPersistedRoutes();
}

// Routes stay in memory until the design is saved. Remove data left by older
// builds so stale panels cannot survive a refresh without their GeoJSON.
export function clearPersistedRoutes() {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function serializeRoutes({ includeGeojson = false } = {}) {
  return Array.from(routeStore.values()).map((route) => ({
    id: route.id,
    name: route.name,
    geojsonUrl: route.geojsonUrl || null,
    ...(includeGeojson && route.geojson ? { geojson: route.geojson } : {}),
    source: route.source || { type: "upload" },
    style: normalizeRouteStyle(route.style),
  }));
}

export function loadRoutes(routes = []) {
  routeStore.clear();
  if (!Array.isArray(routes)) return;
  routes.forEach((route) => {
    const normalized = normalizeRoute(route);
    routeStore.set(normalized.id, normalized);
  });
}

export function getRouteCoordinates(route) {
  if (!route?.geojson?.features) return [];

  return route.geojson.features.flatMap((feature) => {
    const geometry = feature?.geometry;
    if (!geometry) return [];
    if (geometry.type === "LineString") return geometry.coordinates || [];
    if (geometry.type === "MultiLineString") {
      return (geometry.coordinates || []).flatMap((coordinates) => coordinates);
    }
    return [];
  });
}

export function buildRoutesGeojson() {
  const features = [];

  routeStore.forEach((route) => {
    if (!route.geojson?.features) return;

    route.geojson.features.forEach((feature) => {
      if (feature?.geometry?.type !== "LineString") return;
      features.push({
        ...feature,
        properties: {
          ...(feature.properties || {}),
          routeId: route.id,
          color: route.style.color,
          thickness: Number(route.style.thickness) || DEFAULT_STYLE.thickness,
        },
      });
    });
  });

  return { type: "FeatureCollection", features };
}

export function buildRouteEndpointsGeojson() {
  const features = [];

  routeStore.forEach((route) => {
    if (!route.style.showStartPoint && !route.style.showEndPoint) return;

    const lines = (route.geojson?.features || [])
      .filter((feature) => feature?.geometry?.type === "LineString")
      .map((feature) => feature.geometry.coordinates)
      .filter((coordinates) => coordinates?.length);

    if (!lines.length) return;

    const start = lines[0][0];
    const lastLine = lines[lines.length - 1];
    const end = lastLine[lastLine.length - 1];

    const endpoints = [];
    if (route.style.showStartPoint) {
      endpoints.push(["start", start, route.style.startColor]);
    }
    if (route.style.showEndPoint) {
      endpoints.push(["end", end, route.style.endColor]);
    }

    endpoints.forEach(([marker, coordinates, color]) => {
      features.push({
        type: "Feature",
        properties: {
          routeId: route.id,
          marker,
          color,
          routeColor: route.style.color,
        },
        geometry: { type: "Point", coordinates },
      });
    });
  });

  return { type: "FeatureCollection", features };
}
